/**
 * PenetrationInlineCreate (Iter229v · extracted Iter230a)
 * ───────────────────────────────────────────────────────
 * Compact inline form mounted inside the Pin.Flashing (Backtray)
 * dialog. Lets the operator configure the new Penetration's
 * Kind / Usage / Ø / Dektite cascade in one step BEFORE the pin is
 * created — so a single click saves a fully-configured pin and the
 * Flashing modal stays open.
 *
 * `onSave(pinData)` receives the cascade fields only; the parent is
 * responsible for assigning `id`, `x_pct`, `y_pct`, `type`, `color`.
 *
 * Was an inline named export inside PenetrationEditPanel.js until
 * Iter230a — extracted to its own file as part of the tech-debt
 * split. Behaviour is byte-identical; only the import path changed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { KIND_DIAS, VARIANT_OPTS_FOR_ROOFKEY, ridgeRoofKey } from './cascadeHelpers';

export function PenetrationInlineCreate({
  api,
  defaultKind = 'pipe',
  roofProfile,
  roofType,
  onSave,
  onCancel,
}) {
  const roofKey = ridgeRoofKey(roofType);
  const variants = VARIANT_OPTS_FOR_ROOFKEY(roofKey);

  const [kind, setKind] = useState(defaultKind);
  const dias = KIND_DIAS[kind];
  const [usages, setUsages] = useState([]);
  const [pipeUsage, setPipeUsage] = useState('');
  const [dia, setDia] = useState(defaultKind === 'flue' ? 160 : 32);
  const [variant, setVariant] = useState(roofKey === 'iron' ? 'std' : 'lead');
  const [linked, setLinked] = useState({});

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    api.get('/pipe-usages').then(r => {
      if (!cancelled) setUsages(Array.isArray(r?.data) ? r.data : []);
    }).catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, [api]);

  const usagesForKind = usages.filter(u => {
    const scope = (u.pin_type_scope || 'both').toLowerCase();
    if (scope === 'both') return true;
    return scope === kind;
  });

  const cascade = useCallback(async (usageKey, nextDia, nextVariant) => {
    if (!usageKey || !api) { setLinked({}); return; }
    try {
      const { data } = await api.get(`/pipe-usages/${usageKey}/auto-link`, {
        params: { dia: nextDia || 0, variant: nextVariant, sheet_profile: roofProfile || '' },
      });
      setLinked({
        dektite: data?.dektite || null,
        dektiteWarning: data?.dektite_warning || '',
        btray: data?.btray || null,
        flashingTemplate: data?.flashing_template || null,
      });
    } catch {
      setLinked({});
    }
  }, [api, roofProfile]);

  const applyKind = (k) => {
    setKind(k);
    setPipeUsage('');
    setDia(k === 'flue' ? 160 : 32);
    setLinked({});
  };

  const applyUsage = (usageKey) => {
    const usage = usages.find(u => u.key === usageKey);
    const nextDia = usage?.diameter_mm || dia;
    setPipeUsage(usageKey);
    if (usage?.diameter_mm) setDia(usage.diameter_mm);
    cascade(usageKey, nextDia, variant);
  };

  const applyVariant = (v) => {
    setVariant(v);
    if (pipeUsage) cascade(pipeUsage, dia, v);
  };

  const applyDia = (d) => {
    setDia(d);
    if (pipeUsage) cascade(pipeUsage, d, variant);
  };

  // Save is allowed only when we have a real Pipe Usage, a Ø, AND
  // an auto-linked Dektite resolved from the catalogue. Without the
  // last condition we'd let the operator save a "link" that points
  // to nothing — caller then has nothing to add to the Aquaseal
  // section. (Iter233 — was permissive; now strict per the testing
  // agent's UX note.)
  const ready = !!pipeUsage && !!dia && !!linked?.dektite?.id;
  const handleSave = () => {
    if (!ready) return;
    onSave({
      penetration_kind: kind,
      pipe_usage: pipeUsage,
      outlet_diameter_mm: dia,
      dektite_variant: variant,
      linked_dektite_id: linked.dektite?.id,
      linked_dektite_name: linked.dektite?.name,
      linked_dektite_supplier_code: linked.dektite?.supplier_code || '',
      linked_dektite_warning: linked.dektiteWarning || '',
      linked_btray_template_id: linked.btray?.id,
      linked_btray_template_name: linked.btray?.name,
      linked_flashing_template_id: linked.flashingTemplate?.id,
      linked_flashing_name: linked.flashingTemplate?.name || '',
    });
  };

  return (
    <div
      data-testid="penetration-inline-create"
      className="mt-2 p-2 bg-sky-50 ring-1 ring-sky-300 rounded space-y-2"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-bold text-sky-800">
          New Penetration — configure & link
        </div>
        <button
          type="button"
          data-testid="penetration-inline-cancel"
          onClick={onCancel}
          className="text-[10px] text-slate-500 hover:text-slate-700 underline"
        >Cancel</button>
      </div>

      {/* Kind */}
      <div>
        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">❶ Kind</label>
        <div className="flex gap-1.5" data-testid="penetration-inline-kind">
          {[
            { v: 'pipe', label: 'Pipe' },
            { v: 'flue', label: 'Flue' },
          ].map(opt => (
            <button
              key={opt.v}
              type="button"
              data-testid={`penetration-inline-kind-${opt.v}`}
              onClick={() => applyKind(opt.v)}
              className={`flex-1 px-2 py-1 rounded text-[11px] font-semibold ring-1 transition-colors ${
                kind === opt.v
                  ? 'bg-sky-600 text-white ring-sky-600'
                  : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >{opt.label}</button>
          ))}
        </div>
      </div>

      {/* Usage */}
      <div>
        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          ❷ Usage <span className="text-slate-400 font-normal normal-case">— sets Ø</span>
        </label>
        <select
          data-testid="penetration-inline-usage"
          value={pipeUsage}
          onChange={e => applyUsage(e.target.value)}
          className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-white"
        >
          <option value="">— Pick a {kind} usage —</option>
          {usagesForKind.map(u => (
            <option key={u.key} value={u.key}>
              {u.label}{u.diameter_mm ? ` · ${u.diameter_mm} mm` : ''}
            </option>
          ))}
        </select>
        {/* Ø chips */}
        <div className="flex flex-wrap items-center gap-1 mt-1">
          <span className="text-[9px] uppercase font-bold tracking-wider text-slate-500 mr-1">Ø</span>
          {dias.map(d => (
            <button
              key={d}
              type="button"
              data-testid={`penetration-inline-dia-${d}`}
              onClick={() => applyDia(d)}
              className={`px-1.5 py-0.5 text-[10px] font-mono-num rounded ring-1 ${
                dia === d ? 'bg-sky-600 text-white ring-sky-600' : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >{d}</button>
          ))}
        </div>
      </div>

      {/* Dektite */}
      <div>
        <label className="block text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-1">❸ Dektite variant</label>
        <div className={`flex flex-wrap items-center gap-1 ${!pipeUsage ? 'opacity-60 pointer-events-none' : ''}`}>
          {variants.map(opt => (
            <button
              key={opt.v}
              type="button"
              data-testid={`penetration-inline-variant-${opt.v}`}
              onClick={() => applyVariant(opt.v)}
              title={opt.tip}
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${
                variant === opt.v
                  ? (opt.v === 'lead' || opt.v === 'ali' ? 'bg-zinc-700 text-white ring-zinc-700' : 'bg-[#D35400] text-white ring-[#D35400]')
                  : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >{opt.label}</button>
          ))}
        </div>
        {linked.dektite ? (
          <div
            data-testid="penetration-inline-dektite-card"
            className="mt-1.5 bg-emerald-50 ring-1 ring-emerald-200 rounded px-2 py-1 text-[11px]"
          >
            <div className="font-semibold text-emerald-900">✓ {linked.dektite.name}</div>
            {linked.dektite.supplier_code && (
              <div className="text-[10px] font-mono-num text-emerald-700">{linked.dektite.supplier_code}</div>
            )}
            {linked.dektiteWarning && (
              <div className="text-[10px] text-amber-700 italic">{linked.dektiteWarning}</div>
            )}
          </div>
        ) : pipeUsage ? (
          <div className="mt-1.5 text-[10px] text-amber-700 italic">
            No Dektite matches Ø {dia} mm + variant <strong>{variant}</strong>. Catalogue may need an entry.
          </div>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          data-testid="penetration-inline-save"
          onClick={handleSave}
          disabled={!ready}
          className={`px-3 py-1.5 text-[11px] font-bold rounded ${
            ready
              ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >Save Penetration & Link</button>
      </div>
    </div>
  );
}
